/**
 * Fetch + cache the ASL definition for a room's MiniFlow graph.
 *
 * Cache scope: process-lifetime, keyed by ``${networkId}:${releaseVersion}``.
 *
 * Source: ``GET /networks/{id}/releases/{releaseVersion}``. The release
 * row's ``snapshot.interface.asl`` is the canonical replay of the network
 * at the moment ``Create a Room`` was clicked — every room is pinned to
 * a real ``vN`` at creation. The release-detail handler hydrates
 * ``interface.asl`` from S3 when the inline blob is a claim-check
 * pointer (see ``_hydrate_release_asl_inline``).
 */
import { useEffect, useState } from 'react';
import { getNetworkRelease, listNetworkReleases } from '@/services/networkReleasesApi';

const aslCache = new Map();

/** Try every reasonable place a release snapshot could expose the ASL. */
function extractAslFromRelease(detail) {
  if (!detail || typeof detail !== 'object') return null;
  const snap = detail.snapshot && typeof detail.snapshot === 'object' ? detail.snapshot : detail;
  // GENERATIVE networks: the snapshot/interface IS a manifest (org-tree, has
  // rootCohort) rather than an ASL. Return it as-is — MiniFlow renders it via
  // manifestToReactFlow (GENERATIVE-NETWORKS.md §visualization).
  if (Array.isArray(snap?.rootCohort)) return snap;
  const ifaceMaybeManifest = snap?.interface;
  if (ifaceMaybeManifest && Array.isArray(ifaceMaybeManifest.rootCohort)) return ifaceMaybeManifest;
  // Manifest v1: { asl: { rich, deployedHash, ... } }
  const asl1 = snap?.asl;
  if (asl1 && typeof asl1 === 'object') {
    if (asl1.rich && typeof asl1.rich === 'object') return asl1.rich;
    if (asl1.States || asl1.states) return asl1;
  }
  // Release-detail shape (server hydrates S3 artifact inline):
  //   { snapshot: { interface: { asl: {States, StartAt, ...} } } }
  // This is the path the GET /networks/{id}/releases/{v} handler emits
  // after hydrating ``interface.s3Key`` → ``interface.asl``. Older
  // /networks/{id} responses use ``snapshot.network.interface.asl`` —
  // both are accepted so a deploy of either side alone still works.
  const ifaceDirect = snap?.interface;
  if (ifaceDirect && typeof ifaceDirect === 'object') {
    if (ifaceDirect.asl && typeof ifaceDirect.asl === 'object') {
      if (ifaceDirect.asl.rich && typeof ifaceDirect.asl.rich === 'object') return ifaceDirect.asl.rich;
      if (ifaceDirect.asl.States || ifaceDirect.asl.states) return ifaceDirect.asl;
    }
    if (typeof ifaceDirect.asl === 'string') {
      try { return JSON.parse(ifaceDirect.asl); } catch { /* ignore */ }
    }
  }
  // Legacy workbench shape: { network: { interface: { asl: {...} | string } } }
  const iface = snap?.network?.interface;
  if (iface && typeof iface === 'object') {
    if (iface.asl && typeof iface.asl === 'object') {
      if (iface.asl.rich && typeof iface.asl.rich === 'object') return iface.asl.rich;
      return iface.asl;
    }
    if (typeof iface.asl === 'string') {
      try { return JSON.parse(iface.asl); } catch { /* ignore */ }
    }
  }
  return null;
}

// When the caller passes an explicit ``releaseVersion``, key it the same
// way the effect does so a remount inside the same room finds the prior
// fetch synchronously instead of flashing the empty card while the
// effect re-runs.
function primeFromCache(networkId, releaseVersion) {
  if (!networkId) return { asl: null, loading: false, error: null };
  const n =
    releaseVersion != null && releaseVersion !== ''
      ? Math.floor(Number(releaseVersion) || 0)
      : 0;
  if (n >= 1) {
    const key = `${networkId}:${n}`;
    if (aslCache.has(key)) {
      return { asl: aslCache.get(key), loading: false, error: null };
    }
  }
  // Caller didn't pass a version — try the most recent cached entry for
  // this network. The hook will still re-resolve "latest" via the
  // releases list, but this avoids a one-tick flash of the empty card
  // when remounting (e.g. tab switch back to the chat panel).
  for (const [k, v] of aslCache) {
    if (k.startsWith(`${networkId}:`) && v) {
      return { asl: v, loading: false, error: null };
    }
  }
  return { asl: null, loading: false, error: null };
}

export function useNetworkAsl(networkId, releaseVersion) {
  const [state, setState] = useState(() => primeFromCache(networkId, releaseVersion));

  useEffect(() => {
    if (!networkId) {
      setState({ asl: null, loading: false, error: null });
      return undefined;
    }

    let cancelled = false;

    (async () => {
      let version = null;
      if (releaseVersion != null && releaseVersion !== '') {
        const n = Math.floor(Number(releaseVersion) || 0);
        version = n >= 1 ? n : null;
      } else {
        try {
          const { releases } = await listNetworkReleases(networkId, { limit: 1 });
          const first = Array.isArray(releases) ? releases[0] : null;
          const rv = first && typeof first.releaseVersion === 'number' ? first.releaseVersion : null;
          version = rv != null && rv >= 1 ? rv : null;
        } catch (e) {
          console.warn('[useNetworkAsl] listNetworkReleases failed:', e);
          version = null;
        }
      }

      if (cancelled) return;

      if (!version) {
        // Don't blank a previously-good ASL just because the caller
        // momentarily lost ``releaseVersion`` (e.g. parent rooms-list
        // refetch flips ``selected`` to a synthetic stub for a tick).
        // Surface the error but keep ``asl`` if we already had one.
        setState((prev) =>
          prev && prev.asl
            ? { ...prev, loading: false, error: 'No release found' }
            : { asl: null, loading: false, error: 'No release found' },
        );
        return;
      }

      const key = `${networkId}:${version}`;
      if (aslCache.has(key)) {
        setState({ asl: aslCache.get(key), loading: false, error: null });
        return;
      }

      // Hold the previously-resolved ASL while we refetch — the
      // sticky-graph hook downstream does this too, but keeping the
      // value here means a tab-switch remount has the right initial
      // state without a flash.
      setState((prev) => ({
        asl: prev?.asl || null,
        loading: true,
        error: null,
      }));
      try {
        const detail = await getNetworkRelease(networkId, version);
        const asl = extractAslFromRelease(detail);
        if (cancelled) return;
        if (!asl) {
          console.warn('[useNetworkAsl] release detail missing ASL', {
            networkId, version, hasSnapshot: !!detail?.snapshot,
            interfaceKeys: detail?.snapshot?.interface ? Object.keys(detail.snapshot.interface) : [],
          });
        }
        aslCache.set(key, asl);
        setState({ asl, loading: false, error: null });
      } catch (e) {
        if (cancelled) return;
        console.warn('[useNetworkAsl] getNetworkRelease failed:', e);
        setState((prev) =>
          prev && prev.asl
            ? { ...prev, loading: false, error: e.message || 'Failed to load network graph' }
            : { asl: null, loading: false, error: e.message || 'Failed to load network graph' },
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [networkId, releaseVersion]);

  return state;
}

export const __test__ = { extractAslFromRelease };
